import { defineMessages, MessageDescriptor } from 'react-intl';
import { PromoPostFailCode, SerializedScheduledPost } from '../types';
import { platformTakesMedia } from '../platformMeta';

/**
 * The sentence for each failure music24-api can name (`failCode`, from `classifyFailReason`).
 * Every sentence takes `{platform}`. A reason with no code shows as the platform's own words.
 */
export const postFailMessages: Record<PromoPostFailCode, MessageDescriptor> = defineMessages({
  mediaRequired: {
    id: 'app.sml.promote.post.fail.mediaRequired',
    defaultMessage: '{platform} cannot post text only. Add a clip in the Composer and schedule the post again.',
  },
  channelGone: {
    id: 'app.sml.promote.post.fail.channelGone',
    defaultMessage: 'The {platform} channel of this post is gone. Write the post again in the Composer.',
  },
  loginRefreshFailed: {
    id: 'app.sml.promote.post.fail.loginRefreshFailedConnections',
    defaultMessage:
      'We could not refresh your {platform} sign-in. Connect {platform} again in Connections, then schedule the post again.',
  },
  mediaTooLarge: {
    id: 'app.sml.promote.post.fail.mediaTooLarge',
    defaultMessage: 'The clip is too large to send to {platform}. Attach a shorter clip in the Composer.',
  },
  mediaUnreadable: {
    id: 'app.sml.promote.post.fail.mediaUnreadable',
    defaultMessage: 'We could not read the clip of this post. Attach it again in the Composer.',
  },
  platformTimeout: {
    id: 'app.sml.promote.post.fail.platformTimeout',
    defaultMessage: '{platform} did not finish the upload in time. Schedule the post again.',
  },
  // TikTok refusals (`tiktok/tiktokPublishErrors.ts` in music24-api): the stored reason holds the TikTok code.
  tiktokPrivateAccountOnly: {
    id: 'app.sml.promote.post.fail.tiktokPrivateAccountOnly',
    defaultMessage:
      'TikTok lets an unreviewed app post only to a private TikTok account. Set your TikTok account to private, or wait until TikTok approves MUSIC24.',
  },
  tiktokTooManyPosts: {
    id: 'app.sml.promote.post.fail.tiktokTooManyPosts',
    defaultMessage: 'Your TikTok account reached the daily limit of posts from apps. Schedule the post for tomorrow.',
  },
  tiktokBanned: {
    id: 'app.sml.promote.post.fail.tiktokBanned',
    defaultMessage: 'TikTok does not let your account make new posts now. Check your account in the TikTok app.',
  },
  tiktokAppDailyCap: {
    id: 'app.sml.promote.post.fail.tiktokAppDailyCap',
    defaultMessage: 'MUSIC24 reached the daily TikTok limit of posting accounts. Schedule the post for tomorrow.',
  },
  tiktokPrivacyMismatch: {
    id: 'app.sml.promote.post.fail.tiktokPrivacyMismatch',
    defaultMessage:
      'TikTok does not offer the chosen privacy level for your account. Open the post in the Composer and choose who can see it again.',
  },
  tiktokUrlUnverified: {
    id: 'app.sml.promote.post.fail.tiktokUrlUnverified',
    defaultMessage: 'TikTok cannot download the photo from MUSIC24 yet. Post a video, or post the photo by hand.',
  },
  tiktokReconnect: {
    id: 'app.sml.promote.post.fail.tiktokReconnectConnections',
    defaultMessage:
      'TikTok refused the MUSIC24 login. Connect TikTok again in Connections, then schedule the post again.',
  },
  tiktokRateLimited: {
    id: 'app.sml.promote.post.fail.tiktokRateLimited',
    defaultMessage: 'TikTok got too many requests from MUSIC24. Schedule the post again in a few minutes.',
  },
  tiktokMediaRejected: {
    id: 'app.sml.promote.post.fail.tiktokMediaRejected',
    defaultMessage:
      'TikTok did not accept the file (format, length, frame rate or picture size). Attach another file in the Composer.',
  },
  tiktokMediaPullFailed: {
    id: 'app.sml.promote.post.fail.tiktokMediaPullFailed',
    defaultMessage: 'TikTok could not download the file. Schedule the post again.',
  },
  tiktokSpamRisk: {
    id: 'app.sml.promote.post.fail.tiktokSpamRisk',
    defaultMessage: 'TikTok moderation stopped the post. Change the caption or the file in the Composer.',
  },
  tiktokServerError: {
    id: 'app.sml.promote.post.fail.tiktokServerError',
    defaultMessage: 'TikTok had a server error. Schedule the post again.',
  },
});

export const postFailUnknownMessage = defineMessages({
  unknown: {
    id: 'app.sml.promote.post.fail.unknown',
    defaultMessage: '{platform} refused the post. This is what it said:',
  },
}).unknown;

/** The failures a new time cannot fix: the post has to be written again in the Composer. */
const NEEDS_COMPOSER: ReadonlySet<PromoPostFailCode> = new Set<PromoPostFailCode>([
  'mediaRequired',
  'channelGone',
  'mediaTooLarge',
  'mediaUnreadable',
  'tiktokPrivacyMismatch',
  'tiktokUrlUnverified',
  'tiktokMediaRejected',
  'tiktokSpamRisk',
]);

/**
 * True when a failed post cannot go out again as it is. Then the detail offers the Composer,
 * not a new time. A text-only post to a platform that needs media is one such post, whatever
 * its reason says.
 */
export function failedPostNeedsComposer(post: SerializedScheduledPost): boolean {
  if (post.failCode !== null && NEEDS_COMPOSER.has(post.failCode)) {
    return true;
  }
  return post.mediaFileId === null && !platformTakesMedia(post.platform, null);
}

/** The old Channels copy of a failed post. The native apps read this id (navigation v2 renamed the page). */
export const postFailNativeMessages = defineMessages({
  loginRefreshFailed: {
    id: 'app.sml.promote.post.fail.loginRefreshFailed',
    defaultMessage:
      'We could not refresh your {platform} sign-in. Connect {platform} again on the Channels screen, then schedule the post again.',
  },
});
